import { Cross2Icon } from "@radix-ui/react-icons";
import { CheckIcon, HelpCircle, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import ContentArea, { ContentAreaRef } from "@/components/ContentArea";
import { TemplateFormat } from "@/components/Playground/types";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";

interface SnippetFullscreenDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: string;
  onPromptChange: (value: string) => void;
  hasChanged: boolean;
  isReadOnly?: boolean;
  promptName: string;
  version?: number;
  label?: string;
  format?: TemplateFormat;
}

export const SnippetFullscreenDialog = ({
  isOpen,
  onOpenChange,
  prompt,
  onPromptChange,
  hasChanged,
  isReadOnly = false,
  promptName,
  version,
  label,
  format,
}: SnippetFullscreenDialogProps) => {
  const [localPrompt, setLocalPrompt] = useState(prompt);
  const [key, setKey] = useState(0);
  const contentAreaRef = useRef<ContentAreaRef>(null);

  useEffect(() => {
    if (isOpen) {
      setKey(key + 1);
      setLocalPrompt(prompt);
      setTimeout(() => {
        contentAreaRef.current?.focus();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prompt]);

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalPrompt(e.target.value);
  };

  const handleDone = () => {
    onPromptChange(localPrompt);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setLocalPrompt(prompt);
    onOpenChange(false);
  };

  const hasLocalChanges = localPrompt !== prompt;
  const showChangeIndicator = hasChanged || hasLocalChanges;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[90vw] p-0" shouldShowCloseButton={false}>
        <div
          className={`flex items-center justify-between gap-3 rounded-t-lg px-6 py-4 ${
            showChangeIndicator ? "bg-amber-500" : "bg-blue-500"
          }`}
        >
          <DialogClose
            type="button"
            className="absolute right-4 top-4 rounded-sm text-white opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 disabled:pointer-events-none"
          >
            <Cross2Icon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <OverflowDisplayTooltip content={promptName}>
              <span className="min-w-0 truncate font-mono text-base font-medium text-white">
                {promptName}
              </span>
            </OverflowDisplayTooltip>
            {showChangeIndicator && (
              <>
                <span className="flex-shrink-0 text-white">*</span>
                <BasicTooltip
                  content="Changes are only reflected in the playground"
                  variant="black"
                >
                  <HelpCircle className="h-4 w-4 flex-shrink-0 text-white" />
                </BasicTooltip>
              </>
            )}
            <Badge
              variant={showChangeIndicator ? BadgeVariant.AMBER : BadgeVariant.BLUE}
              size={BadgeSize.MD}
              text={showChangeIndicator ? "Latest" : label || (version ? `v${version}` : "Latest")}
              className="flex-shrink-0"
            />
          </div>
        </div>
        <div className="px-6 pb-2 pt-4">
          <div
            className={`h-[60vh] overflow-y-auto rounded-md border border-dashed p-2 ${
              showChangeIndicator ? "border-amber-400" : "border-blue-400"
            }`}
          >
            <ContentArea
              ref={contentAreaRef}
              key={key}
              value={localPrompt}
              onChange={onChange}
              className="h-full w-full min-w-0 border-0 bg-transparent p-0 shadow-none"
              containerClassName="min-w-0 max-h-none h-full"
              size="sm"
              config={{ format }}
              readOnly={isReadOnly}
            />
          </div>
        </div>
        {!isReadOnly && (
          <DialogFooter className="px-6 pb-6">
            <Button variant="outline" onClick={handleCancel}>
              <XIcon className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button onClick={handleDone} disabled={!hasLocalChanges}>
              <CheckIcon className="mr-2 h-4 w-4" />
              Done
            </Button>
          </DialogFooter>
        )}
        {isReadOnly && (
          <DialogFooter className="px-6 pb-6">
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
